import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ClockIcon, ArrowUpRightIcon } from 'lucide-react';
import { programs, contact } from '../data/site';
import { useLanguage } from '../contexts/LanguageContext';
import { useMotionPreferences } from '../hooks/useMotionPreferences';
import { fadeInX, fadeUp, staggerContainer, viewportOnce } from '../utils/motion';
const accents = {
  blue: {
    dot: 'bg-electric',
    glow: 'group-hover:shadow-glow-blue',
    border: 'group-hover:border-electric/60',
    text: 'text-electric'
  },
  magenta: {
    dot: 'bg-magenta',
    glow: 'group-hover:shadow-glow-magenta',
    border: 'group-hover:border-magenta/60',
    text: 'text-magenta'
  },
  purple: {
    dot: 'bg-grape',
    glow: 'group-hover:shadow-[0_0_40px_-8px_rgba(122,43,224,0.7)]',
    border: 'group-hover:border-grape/60',
    text: 'text-[#A46BFF]'
  }
};
export function Programs() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    reduced,
    parallax
  } = useMotionPreferences();
  const {
    t
  } = useLanguage();
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const auraY = useTransform(scrollYProgress, [0, 1], [60 * parallax, -60 * parallax]);
  const heading = fadeUp(reduced, 28);
  const row = fadeInX(reduced, -28);
  return <section id="programs" ref={sectionRef} className="relative w-full overflow-hidden border-y border-white/5 bg-navy py-28" data-id="element-155" data-mp="ebi0x7wrhcg3">
      <div className="grid-lines absolute inset-0 opacity-40" data-id="element-156" data-mp="171yyozet1g9k" />
      <motion.div aria-hidden="true" style={{
      y: auraY,
      willChange: 'transform'
    }} className="pointer-events-none absolute inset-0" data-id="element-157" data-mp="170ffp11rkr43">
        <div className="absolute -left-24 top-1/3 h-96 w-96 rounded-full bg-electric/15 blur-[150px]" data-id="element-158" data-mp="y10ry9czeuna" />
        <div className="absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-magenta/15 blur-[150px]" data-id="element-159" data-mp="3muxv4ig8dudo" />
      </motion.div>

      <div className="relative mx-auto max-w-6xl px-5 sm:px-8" data-id="element-160" data-mp="3iu5n1sj3hviz">
        <motion.div variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce} className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-161" data-mp="3k9hthmooexr0">
          <div data-id="element-162" data-mp="3f9lp3mc2h4ax">
            <motion.p variants={heading} className="text-xs font-semibold uppercase tracking-[0.28em] text-electric" data-id="element-163" data-mp="3f9c64ipfvxeq">
              {t.programs.eyebrow}
            </motion.p>
            <motion.h2 variants={heading} className="neon-text mt-4 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl" data-id="element-164" data-mp="36a8n1eq9vi08">
              {t.programs.title}
            </motion.h2>
          </div>
          <motion.p variants={heading} className="max-w-sm text-sm leading-relaxed text-white/55" data-id="element-165" data-mp="2ijuvrqfihn6p">
            {t.programs.note}
          </motion.p>
        </motion.div>

        <motion.ul variants={staggerContainer(0.07, 0.06)} initial="hidden" whileInView="show" viewport={viewportOnce} className="mt-14 space-y-3" data-id="element-166" data-mp="23wmhuhutj3r1">
          {programs.map((program, i) => {
          const accent = accents[program.accent];
          const copy = t.programs.rows[i];
          return <motion.li key={program.day} variants={row} data-id="element-167" data-mp="280u5sctgcfo7" data-mpi={i}>
                <motion.a href={contact.bookingHref} target="_blank" rel="noopener noreferrer" whileHover={reduced ? {
              opacity: 0.95
            } : {
              x: 6,
              scale: 1.008
            }} whileTap={reduced ? undefined : {
              scale: 0.995
            }} transition={{
              type: 'spring',
              stiffness: 320,
              damping: 24
            }} style={{
              willChange: 'transform'
            }} className={`glass group flex flex-col gap-4 rounded-2xl px-6 py-6 transition-[box-shadow,border-color] duration-300 sm:flex-row sm:items-center sm:justify-between ${accent.border} ${accent.glow}`} data-id="element-168" data-mp="2mugwbbepu7gt">
                  <div className="flex items-center gap-4 sm:w-56" data-id="element-169" data-mp="3oqcuhmmpyp51">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${accent.dot}`} data-id="element-170" data-mp="3ooi803vsuoyu" />
                    <span className="font-display text-lg font-extrabold uppercase tracking-[0.08em] text-white" data-id="element-171" data-mp="3tjo4txqqpo1p">
                      {copy.day}
                    </span>
                  </div>

                  <div className="flex-1 sm:px-6" data-id="element-172" data-mp="31shzn2w5x05k">
                    <p className="font-display text-base font-bold uppercase tracking-[0.06em] text-white/90" data-id="element-173" data-mp="35r8okmqh5bhl">
                      {copy.name}
                    </p>
                    <p className={`mt-1 text-xs font-semibold uppercase tracking-[0.16em] ${accent.text}`} data-id="element-174" data-mp="3fq6jq8ieo39k">
                      {copy.level}
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-6 sm:justify-end" data-id="element-175" data-mp="uz60pukch7p8">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-white/75" data-id="element-176" data-mp="2fi9ks7i4at15">
                      <ClockIcon className="h-4 w-4 text-white/40" data-id="element-177" data-mp="2ain9w4prhovi" />
                      {copy.time}
                    </span>
                    <ArrowUpRightIcon className="h-5 w-5 text-white/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" data-id="element-178" data-mp="25qlvod919o2u" />
                  </div>
                </motion.a>
              </motion.li>;
        })}
        </motion.ul>
      </div>
    </section>;
}